import { Campaign, Creator } from "@/types/types";
import { EvePipelinePhase, EvePhasePayment, EvePhaseState, EveTierPipeline } from "@/types/eve";

/**
 * Amy-v2 (eve) helpers — status-shape parsing ported from
 * amy/lib/domain/statuses.ts (the source of truth; keep in sync), the
 * pipeline default resolution from amy/lib/domain/phases.ts, and the
 * per-tier pipeline resolver from amy/lib/domain/tierPipelines.ts.
 */

/* ── Per-tier pipelines (2026-10-16) — port of amy/lib/domain/tierPipelines.ts ──
 * A campaign has ONE post-activation pipeline PER PARTNERSHIP TIER
 * (campaigns.eve_tier_pipelines, keyed by the tier's `order`) and a creator
 * runs the pipeline of the tier they closed in. These functions are the ONLY
 * code that reads the stored arrays — every component asks them:
 *   pipelineForCreator(campaign, creator) → the phases ONE row runs (cells,
 *                                           badges, dialogs, breadcrumbs)
 *   pipelineForTier(campaign, tier)       → one tier's phases (the editor)
 *   allPhases(campaign)                   → the union across tiers, for
 *                                           campaign-wide code (table columns,
 *                                           status-filter labels)
 * Keep them in step with amy's module and the Flask copy
 * (backend/services/campaigns.py _eve_pipeline_for_tier & co).
 */

/** The campaign fields the per-tier resolver reads. */
export type PipelineCampaign = Pick<Campaign, "partnership" | "eve_tier_pipelines">;

/** The campaign's partnership tier orders, ascending; [1] when it defines none. */
export function tierOrders(campaign: Pick<Campaign, "partnership"> | null | undefined): number[] {
  const orders = (campaign?.partnership?.tiers ?? [])
    .map((t) => t.order)
    .filter((o): o is number => typeof o === "number");
  return orders.length > 0 ? [...new Set(orders)].sort((a, b) => a - b) : [1];
}

/**
 * The tier a creator's pipeline resolves on — amy's resolvePartnership rule
 * (amy/lib/domain/tiers.ts:30-31), the same one the activation message and
 * the contract offer use, so what the creator was told and what they run can
 * never disagree: their stored closing tier when a tier with that order
 * exists, else the FIRST tier (lowest order), else 1. A missing field or a
 * tier removed since both land on the first tier.
 */
export function creatorTierOrder(
  campaign: Pick<Campaign, "partnership"> | null | undefined,
  creator: Pick<Creator, "current_tier_order"> | null | undefined
): number {
  const tiers = [...(campaign?.partnership?.tiers ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const requested = creator?.current_tier_order ?? 1;
  const current = tiers.find((t) => t.order === requested) ?? tiers[0];
  return current?.order ?? 1;
}

/**
 * One tier's phases. A tier without an element — or a campaign without an
 * `eve_tier_pipelines` array at all — has NO pipeline ([]). (The legacy
 * single `eve_pipeline` was migrated into per-tier copies on 2026-10-17 and
 * is no longer emitted by Flask.)
 */
export function pipelineForTier(campaign: PipelineCampaign | null | undefined, tier: number): EvePipelinePhase[] {
  const groups = campaign?.eve_tier_pipelines;
  return Array.isArray(groups) ? (groups.find((g) => g.tier === tier)?.phases ?? []) : [];
}

/** The phases ONE creator runs: their closing tier's pipeline. */
export function pipelineForCreator(
  campaign: PipelineCampaign | null | undefined,
  creator: Pick<Creator, "current_tier_order"> | null | undefined
): EvePipelinePhase[] {
  return pipelineForTier(campaign, creatorTierOrder(campaign, creator));
}

/** Every tier's pipeline, in tier order (a legacy single pipeline expands to every tier). */
export function pipelineGroups(campaign: PipelineCampaign | null | undefined): EveTierPipeline[] {
  return tierOrders(campaign).map((tier) => ({ tier, phases: pipelineForTier(campaign, tier) }));
}

/**
 * The union of all tiers' phases by key, sorted by order — for code that
 * must consider every creator of a campaign at once (table columns,
 * status-filter labels). An ENABLED entry wins over a disabled one with the
 * same key (a phase SOME tier runs is a phase the campaign runs); otherwise
 * the lowest tier's entry represents the key. Per-tier settings differ —
 * read them per creator (pipelineForCreator).
 */
export function allPhases(campaign: PipelineCampaign | null | undefined): EvePipelinePhase[] {
  const byKey = new Map<string, EvePipelinePhase>();
  for (const group of pipelineGroups(campaign)) {
    for (const p of group.phases) {
      const seen = byKey.get(p.key);
      if (!seen || (seen.enabled !== true && p.enabled === true)) byKey.set(p.key, p);
    }
  }
  return [...byKey.values()].sort((a, b) => evePhaseOrder(a) - evePhaseOrder(b));
}

/**
 * Order-independent fingerprint of a pipeline (phases by key, object keys
 * sorted): equal fingerprints = the same pipeline. The Pipeline tab compares
 * tiers with it — a copy made by the executor and an edit that merely
 * re-ordered an object's keys must still read as identical.
 */
export function canonicalPipeline(phases: EvePipelinePhase[]): string {
  const canon = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(canon);
    if (value && typeof value === "object") {
      const record = value as Record<string, unknown>;
      return Object.fromEntries(
        Object.keys(record)
          .sort()
          .map((k) => [k, canon(record[k])])
      );
    }
    return value;
  };
  return JSON.stringify([...phases].sort((a, b) => a.key.localeCompare(b.key)).map(canon));
}

/** Are every tier's phases identical? (the Pipeline tab's "shared by all tiers" mode) */
export function sameForAllTiers(campaign: PipelineCampaign | null | undefined): boolean {
  const groups = pipelineGroups(campaign);
  if (groups.length <= 1) return true;
  const first = canonicalPipeline(groups[0].phases);
  return groups.every((g) => canonicalPipeline(g.phases) === first);
}

/** True when Amy v2 runs the campaign (backend derives is_eve from eve_autopilot PRESENCE). */
export function isEveCampaign(campaign?: Pick<Campaign, "is_eve"> | null): boolean {
  return campaign?.is_eve === true;
}

/** Ignore repopulating stamps older than this — a crashed search must not spin forever. */
const REPOPULATING_STALE_MS = 30 * 60_000;

/**
 * True while Amy v2's find_creators_for_campaign is filling the roster of an
 * EXISTING campaign (`repopulating_since` stamped by the tool, cleared by the
 * search worker when the task finishes). Drives the non-blocking "Finding
 * creators" indicator — unlike POPULATING, the campaign stays fully usable.
 */
export function isRepopulatingCampaign(campaign?: { repopulating_since?: string | Date | null } | null): boolean {
  if (!campaign?.repopulating_since) return false;
  const since = new Date(campaign.repopulating_since).getTime();
  return Number.isFinite(since) && Date.now() - since < REPOPULATING_STALE_MS;
}

/** Custom phase keys: x-<slug>, never ending in a state suffix. */
export const CUSTOM_PHASE_KEY_RE = /^x-[a-z0-9][a-z0-9-]{1,22}$/;

export function isCustomPhaseKey(key: string): boolean {
  return (
    CUSTOM_PHASE_KEY_RE.test(key) &&
    !key.endsWith("-review") &&
    !key.endsWith("-run") &&
    !key.endsWith("-wait")
  );
}

export type CustomStatusKind = "entry" | "review" | "run" | "wait";

/** The four custom-phase status SHAPES: waiting-x-<slug> / x-<slug>-review / x-<slug>-run / x-<slug>-wait. */
export function customStatusKind(status: string): CustomStatusKind | null {
  if (status.startsWith("waiting-") && isCustomPhaseKey(status.slice("waiting-".length))) return "entry";
  if (status.endsWith("-review") && isCustomPhaseKey(status.slice(0, -"-review".length))) return "review";
  if (status.endsWith("-run") && isCustomPhaseKey(status.slice(0, -"-run".length))) return "run";
  if (status.endsWith("-wait") && isCustomPhaseKey(status.slice(0, -"-wait".length))) return "wait";
  return null;
}

/** Recover the pipeline key (x-<slug>) from a custom-shape status. */
export function customKeyForStatus(status: string): string | null {
  const kind = customStatusKind(status);
  if (!kind) return null;
  if (kind === "entry") return status.slice("waiting-".length);
  if (kind === "review") return status.slice(0, -"-review".length);
  if (kind === "run") return status.slice(0, -"-run".length);
  return status.slice(0, -"-wait".length);
}

export const CUSTOM_STATE_LABELS: Record<CustomStatusKind, string> = {
  entry: "waiting on creator",
  review: "in review",
  run: "running action",
  wait: "waiting on event",
};

/** The phase key a phase-pipeline status belongs to (built-ins + custom shapes). */
export function evePhaseKeyForStatus(status: string): string | null {
  const builtIn: Record<string, string> = {
    "pending-contract": "contract",
    "waiting-signature": "contract",
    "pending-payment-1": "payment_1",
    "waiting-payment-1": "payment_1",
    "pending-payment-2": "payment_2",
    "waiting-payment-2": "payment_2",
    "waiting-script": "script",
    "script-review": "script",
    "waiting-draft": "draft",
    "draft-review": "draft",
    "waiting-posting": "posting",
    "posting-review": "posting",
    "pending-payment": "payment",
    "waiting-payment": "payment",
  };
  return builtIn[status] ?? customKeyForStatus(status);
}

/** Default phase orders — amy/lib/domain/phases.ts DEFAULT_ORDERS (spaced by 10). */
export const EVE_PHASE_DEFAULT_ORDERS: Record<string, number> = {
  contract: 10,
  payment_1: 15,
  script: 20,
  draft: 30,
  payment_2: 32,
  posting: 35,
  payment: 40,
};

/** The payout-slot family (amy/lib/domain/payoutMath.ts PAYOUT_SLOTS): early split sub-payments + the final remainder payment. */
export const EVE_PAYOUT_SLOT_KEYS = ["payment_1", "payment_2", "payment"] as const;

export function isEvePayoutSlotKey(key: string): boolean {
  return (EVE_PAYOUT_SLOT_KEYS as readonly string[]).includes(key);
}

/** Enabled payout slots on a pipeline, in payment_1 → payment_2 → payment order. */
export function enabledEvePayoutSlotKeys(pipeline?: EvePipelinePhase[] | null): string[] {
  return EVE_PAYOUT_SLOT_KEYS.filter((k) => (pipeline ?? []).some((p) => p.key === k && p.enabled === true));
}

/** The payout slot's per-creator state subdoc (eve_phase.payment_1/payment_2/payment). */
export function evePayoutState(
  evePhase: EvePhaseState | undefined | null,
  key: string
): EvePhasePayment | undefined {
  if (key === "payment") return evePhase?.payment ?? undefined;
  if (key === "payment_1") return evePhase?.payment_1 ?? undefined;
  if (key === "payment_2") return evePhase?.payment_2 ?? undefined;
  return undefined;
}

/**
 * Ordinal display name for a payout slot when the fee is split — "Payment 1",
 * "Payment 2", the final slot taking the last number; null when the campaign
 * has at most one payout slot (plain "Payment" labels stay as-is). Counts
 * ENABLED slots by default (right for creator STATUS labels — statuses only
 * exist on enabled phases); pass includeDisabled for pipeline-config labels,
 * so a just-added, still-disabled split payment renumbers the nav
 * immediately instead of sitting next to a plain "Payment".
 */
export function evePayoutOrdinalLabel(
  key: string,
  pipeline?: EvePipelinePhase[] | null,
  opts?: { includeDisabled?: boolean }
): string | null {
  if (!isEvePayoutSlotKey(key)) return null;
  const slots: string[] = opts?.includeDisabled
    ? EVE_PAYOUT_SLOT_KEYS.filter((k) => (pipeline ?? []).some((p) => p.key === k))
    : enabledEvePayoutSlotKeys(pipeline);
  if (slots.length <= 1) return null;
  const idx = slots.indexOf(key);
  return `Payment ${idx === -1 ? slots.length : idx + 1}`;
}

export function evePhaseOrder(entry: EvePipelinePhase): number {
  return entry.order ?? EVE_PHASE_DEFAULT_ORDERS[entry.key] ?? 999;
}

export function evePhaseLabel(entry: EvePipelinePhase, pipeline?: EvePipelinePhase[] | null): string {
  if (entry.label) return entry.label;
  const ordinal = evePayoutOrdinalLabel(entry.key, pipeline, { includeDisabled: true });
  if (ordinal) return ordinal;
  if (entry.key === "payment_1") return "Payment 1";
  if (entry.key === "payment_2") return "Payment 2";
  return entry.key.charAt(0).toUpperCase() + entry.key.slice(1);
}

/** Resolved settings with amy's defaults (resolvePhaseSettings parity). */
export function resolveEvePhaseSettings(entry: EvePipelinePhase): {
  nudgeEnabled: boolean;
  maxNudges: number;
  nudgeWaitSeconds: number;
  judgeMode: "auto" | "approval" | "assist";
  handoffAfterRevisions: number;
  percent: number;
} {
  const s = entry.settings ?? {};
  const posting = entry.key === "posting";
  return {
    nudgeEnabled: s.nudge_enabled ?? true,
    maxNudges: s.max_nudges ?? (posting ? 5 : 2),
    nudgeWaitSeconds: s.nudge_wait_seconds ?? (posting ? 3 : 2) * 24 * 3600,
    judgeMode: s.judge_mode ?? "approval",
    handoffAfterRevisions: s.handoff_after_revisions ?? 3,
    // Early payout slots only (amy PHASE_KEY_DEFAULTS: payment_1=50, payment_2=25).
    percent: s.percent ?? (entry.key === "payment_2" ? 25 : 50),
  };
}
